// Copyright (c) Ping. All rights reserved.

import React, { useCallback, useMemo } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Avatar } from '../../components/Avatar';
import { createTheme } from '../../design-system/theme';
import { activityStrings } from './strings';
import type { ActivityItem as ActivityItemRecord, ActivityRowKind } from './types';

export interface ActivityItemProps {
  readonly item: ActivityItemRecord;
  readonly onPress?: ((item: ActivityItemRecord) => void) | undefined;
}

/** Single activity-feed row matching the Figma `Activity Item/ desktop` set. */
export const ActivityItem = React.memo<ActivityItemProps>(({ item, onPress }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const handlePress = useCallback(() => {
    onPress?.(item);
  }, [item, onPress]);

  const formattedTime = useMemo(() => formatRelative(item.timestamp), [item.timestamp]);

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`${activityStrings.rowKindLabel(item.kind)}, ${formattedTime}`}
      style={({ pressed }) => [
        styles.row,
        {
          backgroundColor: theme.colors.surface.primary,
          borderBottomColor: theme.colors.border.secondary,
          opacity: pressed ? 0.85 : 1,
        },
      ]}
    >
      <View style={styles.avatarWrap}>
        <Avatar
          size="medium"
          initials={item.actorName ?? '·'}
          presence={null}
          accessibilityLabel={item.actorName ?? activityStrings.rowKindLabel(item.kind)}
        />
        <View
          style={[
            styles.kindGlyph,
            { backgroundColor: kindGlyphColor(item.kind, theme.colors.accent) },
          ]}
          accessibilityElementsHidden
        >
          <Text style={styles.kindGlyphText}>{kindGlyph(item.kind)}</Text>
        </View>
      </View>

      <View style={styles.body}>
        <View style={styles.topRow}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]} numberOfLines={1}>
            {item.actorName ?? activityStrings.rowKindLabel(item.kind)}
          </Text>
          <Text style={[styles.timestamp, { color: theme.colors.text.tertiary }]}>
            {formattedTime}
          </Text>
        </View>
        <Text style={[styles.preview, { color: theme.colors.text.secondary }]} numberOfLines={2}>
          {item.previewText}
        </Text>
      </View>

      {item.unread && (
        <View
          style={[styles.unreadDot, { backgroundColor: theme.colors.accent.indigo }]}
          accessibilityLabel="Unread"
        />
      )}
    </Pressable>
  );
});

ActivityItem.displayName = 'ActivityItem';

function kindGlyph(kind: ActivityRowKind): string {
  switch (kind) {
    case 'dm':
      return '✉';
    case 'mention':
      return '@';
    case 'reminder':
      return '◷';
    case 'missedCall':
      return '☎';
    case 'reaction':
      return '✦';
    case 'task':
      return '✓';
    case 'system':
      return '·';
    case 'reply':
      return '↩';
    case 'game':
      return '🎮';
    case 'poll':
      return '📊';
    case 'meeting':
      return '📅';
  }
}

function kindGlyphColor(
  kind: ActivityRowKind,
  accent: {
    indigo: string;
    rose: string;
    mint: string;
    amber: string;
    violet: string;
    teal: string;
    pink: string;
    green: string;
    coral: string;
    slate: string;
  },
): string {
  switch (kind) {
    case 'dm':
      return accent.indigo;
    case 'mention':
      return accent.violet;
    case 'reminder':
      return accent.amber;
    case 'missedCall':
      return accent.coral;
    case 'reaction':
      return accent.pink;
    case 'task':
      return accent.mint;
    case 'system':
      return accent.slate;
    case 'reply':
      return accent.teal;
    case 'game':
      return accent.green;
    case 'poll':
      return accent.indigo;
    case 'meeting':
      return accent.rose;
  }
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Compact relative-time string that doesn't lean on `Intl.RelativeTimeFormat`
 * (Hermes/macOS tooling support is patchy). Pure helper, exported via tests.
 */
export function formatRelative(timestamp: number, now: number = Date.now()): string {
  const delta = now - timestamp;
  if (delta < 0) {
    return 'now';
  }
  if (delta < HOUR_MS) {
    const m = Math.max(1, Math.floor(delta / 60_000));
    return `${m}m`;
  }
  if (delta < DAY_MS) {
    return `${Math.floor(delta / HOUR_MS)}h`;
  }
  if (delta < WEEK_MS) {
    return `${Math.floor(delta / DAY_MS)}d`;
  }
  return new Date(timestamp).toISOString().slice(0, 10);
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  avatarWrap: {
    width: 44,
    height: 44,
    marginRight: 12,
  } satisfies ViewStyle,
  kindGlyph: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  kindGlyphText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  } satisfies TextStyle,
  body: {
    flex: 1,
  } satisfies ViewStyle,
  topRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 2,
  } satisfies ViewStyle,
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    marginRight: 8,
  } satisfies TextStyle,
  timestamp: {
    fontSize: 11,
    fontWeight: '500',
  } satisfies TextStyle,
  preview: {
    fontSize: 13,
    lineHeight: 18,
  } satisfies TextStyle,
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginLeft: 8,
    marginTop: 8,
  } satisfies ViewStyle,
});
